import axios, { AxiosError, AxiosInstance } from 'axios';
import { useUserAuthStore } from '@/store/userAuthStore';

const API_URL = import.meta.env.VITE_API_URL || '/api';

export const TOKEN_ROUTING = [
  { prefix: '/admin',          key: 'token' },
  { prefix: '/dealer',         key: 'dealerToken' },
  { prefix: '/favorites',      key: 'userToken' },
  { prefix: '/saved-searches', key: 'userToken' },
  { prefix: '/notifications',  key: 'userToken' },
  { prefix: '/devices',        key: 'userToken' },
  // /auth/me is guarded by userAuthMiddleware and needs the user token. It must
  // precede the public '/auth' rule below (first prefix match wins) or session
  // restore on reload always 401s. The other /auth/* endpoints stay token-less.
  { prefix: '/auth/me',        key: 'userToken' },
  { prefix: '/companies',      key: '' },
  { prefix: '/leads',          key: '' },
  { prefix: '/auth',           key: '' },
] as const;

export function resolveTokenForUrl(url: string): string | null {
  const normalised = url.startsWith('http') ? new URL(url).pathname : url;
  const match = TOKEN_ROUTING.find(r => normalised.startsWith(r.prefix));
  if (!match || !match.key) return null;
  return localStorage.getItem(match.key);
}

function clearTokenForUrl(url: string): void {
  if (url.startsWith('/admin')) {
    localStorage.removeItem('token');
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/admin/login')) {
      window.location.href = '/admin/login';
    }
  } else if (url.startsWith('/dealer')) {
    localStorage.removeItem('dealerToken');
    localStorage.removeItem('dealerRefreshToken');
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/dealer/login')) {
      window.location.href = '/dealer/login';
    }
  } else if (url.startsWith('/favorites') || url.startsWith('/saved-searches')) {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userRefreshToken');
    useUserAuthStore.getState().logout();
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

apiClient.interceptors.request.use(
  (config) => {
    const token = resolveTokenForUrl(config.url ?? '');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => {
    const data = response.data as { success?: boolean; error?: string } | undefined;
    if (data?.success === false && (data?.error === 'Invalid or expired token' || data?.error?.toLowerCase().includes('expired token'))) {
      clearTokenForUrl(response.config?.url ?? '');
    }
    return response;
  },
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      clearTokenForUrl(error.config?.url ?? '');
    }
    return Promise.reject(error);
  }
);

export function handleApiError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.error || error.response?.data?.message;
    if (message) return message;
    if (error.response?.status === 404) return 'Resource not found';
    if (error.response?.status === 401) return 'Unauthorized';
    if (error.response?.status === 403) return 'Forbidden';
    if (error.response?.status === 429) return 'Too many requests. Please try again later.';
    if (error.response?.status && error.response.status >= 500) return 'Server error. Please try again later.';
  }
  return 'An unexpected error occurred';
}

/**
 * Extract the backend response body from a failed request, when the server
 * responded with a structured error (e.g. `{ success: false, error, data }`).
 * Returns undefined for non-Axios errors or network failures with no response.
 */
export function getApiErrorBody(
  error: unknown
): { error?: string; data?: { requiresVerification?: boolean } } | undefined {
  if (axios.isAxiosError(error)) {
    return error.response?.data as
      | { error?: string; data?: { requiresVerification?: boolean } }
      | undefined;
  }
  return undefined;
}
